import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import CartContents from './CartContents';
import CartVoucherItems from './CartVoucherItems';
import OrderSummary from './OrderSummary';
import NewOrder from './NewOrder';
import KeepShoppingButton from './KeepShoppingButton';
import { resetAdditionalData } from '../../../reducers/additionalDataSlice';
import cartUtils from '../../../utils/cart';
import { removeCartItem } from '../../../actions/v3Order/v3Order';
import { selectFreeShippingIncentiveProducts } from '../../../selectors';
import { isShippingRestrictedForParent } from '../helpers/internationalRestriction';

const CartHeader = styled.h4`
  margin: 0;
  font-size: 24px;
`;

const CartDivider = styled.hr`
  margin: 12px 0 20px 0;
  border-top: 1px solid #ddd;
`;

const CartList = styled.ul`
  @media (max-width: 767px) {
    padding: 0;
  }
`;

const Cart = ({ history, restored = false }) => {
  const dispatch = useDispatch();

  const orders = useSelector((state) => state.v3Order.orders);
  const company = useSelector((state) => state.company);
  const cartVoucherItems = useSelector((state) => state.cartVoucherItems);
  const shoot = useSelector((state) => state.shoot?.shoot);
  const shootProducts = useSelector((state) => state.shoot?.shootProducts);
  const parent = useSelector((state) => state.parent);

  // A price list is only "active" when the shoot's products have loaded. When
  // it hasn't (e.g. clicking the cart icon from the dashboard with nothing in
  // progress), redirecting to /v3/offers renders/crashes with no products —
  // so send those customers back to the dashboard instead. See ENG-2607.
  //
  // There is no "still loading" state to tell apart here: the shoot slice is
  // never rehydrated from localStorage (persist migration 4 in packs/index.js)
  // and getShootData is only dispatched from the dashboard / key-entry flows,
  // which navigate into the ordering flow after it resolves — so with an empty
  // cart, shootProducts is either already populated or will not populate while
  // this screen is mounted.
  const hasActivePriceList =
    Array.isArray(shootProducts) && shootProducts.length > 0;
  const country = parent?.address?.country || 'US';
  const restricted = isShippingRestrictedForParent(shoot, parent);

  const hasRegularProducts = !cartUtils.allOrdersAreEmpty(orders);
  const allVoucherItems = cartVoucherItems.items || [];
  const unassignedVoucherItems = allVoucherItems.filter(
    (item) => !item.order_key,
  );
  const hasAnyVouchers = allVoucherItems.length > 0;

  // When regular products exist, order-keyed vouchers appear under their StudentOrder.
  // When no regular products exist, show all vouchers in the cart-wide section so
  // order-keyed items remain visible and removable.
  const cartWideVoucherItems = hasRegularProducts
    ? unassignedVoucherItems
    : allVoucherItems;
  const hasCartWideVouchers = cartWideVoucherItems.length > 0;

  // Redirect at most once per mount: hasActivePriceList is a dependency, so if
  // shoot data ever did resolve while the empty cart was still mounted the
  // effect would re-run and issue a second, contradictory push.
  const emptyCartRedirected = useRef(false);

  useEffect(() => {
    if (
      !hasRegularProducts &&
      !hasAnyVouchers &&
      !emptyCartRedirected.current
    ) {
      emptyCartRedirected.current = true;
      // dispatch(resetAdditionalData());
      history.push(hasActivePriceList ? '/v3/offers' : '/dashboard');
    }
  }, [
    orders,
    cartVoucherItems.items,
    history,
    hasRegularProducts,
    hasAnyVouchers,
    hasActivePriceList,
  ]);

  // Every list in the cart, not just the active shoot's: a free-shipping row
  // stashed from another school is just as unusable for a restricted parent,
  // and its id is not in the active catalog (ENG-2043 R6).
  const freeShippingIncentiveProducts = useSelector(
    selectFreeShippingIncentiveProducts,
  );

  useEffect(() => {
    if (!restricted) return;
    const toRemove = cartUtils.removableShippingIncentives(
      orders,
      country,
      freeShippingIncentiveProducts,
    );
    toRemove.forEach(({ orderKey, cartItemId }) => {
      // The parent did not choose this — it must not be recorded as a
      // decline, or the bonus would stay suppressed once they order from a
      // US address.
      dispatch(removeCartItem(cartItemId, orderKey, { recordDecline: false }));
    });
  }, [restricted, orders, country, dispatch, freeShippingIncentiveProducts]);

  return (
    <div className="bnl-order-cart">
      <CartHeader>Your Cart</CartHeader>
      {restored && (
        <p role="status">
          Your saved cart replaced the cart on this device. Please review its
          items before checkout.
        </p>
      )}
      <CartDivider />
      <div className="bnl-order-cart-container row">
        <CartList className="bnl-order-cart__content col-xs-12 col-md-8 col-lg-8">
          {hasRegularProducts && <CartContents orders={orders} />}
          {hasCartWideVouchers && (
            <CartVoucherItems items={cartWideVoucherItems} />
          )}
        </CartList>
        <div className="bnl-order-cart__right col-xs-12 col-md-4 col-lg-4 clearfix">
          <OrderSummary orders={orders} />
          <NewOrder company={company} />
          <KeepShoppingButton />
        </div>
      </div>
    </div>
  );
};

export default Cart;
